import { RouteObject } from 'react-router-dom';
import { Auth0ProviderWithNavigate } from '../auth0-provider';
import { RootErrorBoundary } from '../components/errors/RootErrorBoundary';
import { AdminLayout } from './layouts/AdminLayout';
import { lazyNamed } from './lazyNamed';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import { AdminViewOnly } from '../Views/Admin/AdminViewOnly';
import { AdminFeedbackSessionLoader } from '../loaders/AdminFeedbackSessionLoader';
import { AdminNoteLoader } from '../loaders/NoteLoader';
import { ModeratorView } from '../Views/Admin/ModeratorView';
import { SelectedUserProvider } from '../context/selectedUserContext.tsx';
import { TemplateLoader } from '../loaders/TemplatesLoader';
import { BaseTemplateLoader } from '../loaders/TemplatesLoader';
import { PublicTemplateLoader } from '../loaders/TemplatesLoader';
import { UserTemplateLoader } from '../loaders/TemplatesLoader';
import { ModuleLoader } from '../loaders/ModulesLoader';

const UserUploadedTemplates = lazyWithRetry(() => import('../Views/Admin/UserUploadedTemplates'));
const GenerateNoteForUser = lazyWithRetry(() => import('../Views/Admin/GenerateNoteForUser'));
const CohortDefinitionsView = lazyWithRetry(() => import('../Views/Admin/CohortDefinitionsView'));
const AnalysisSessionInspector = lazyWithRetry(
  () => import('../Views/Admin/AnalysisSessionInspector'),
);
const FeedbackOverview = lazyNamed(
  () => import('../Views/Admin/FeedbackOverview'),
  'FeedbackOverview',
);
const AdminFeedbackSessionView = lazyWithRetry(
  () => import('../Views/Admin/AdminFeedbackSessionView'),
);
const NotesOverview = lazyNamed(() => import('../Views/Admin/NotesOverview'), 'NotesOverview');
const AdminNoteView = lazyWithRetry(() => import('../Views/Admin/AdminNoteView'));
const SpecialtiesOverview = lazyNamed(
  () => import('../Views/Admin/SpecialtiesOverview'),
  'SpecialtiesOverview',
);
const BaseTemplatesOverview = lazyNamed(
  () => import('../Views/Admin/BaseTemplatesOverview'),
  'BaseTemplatesOverview',
);
const PublicTemplatesOverview = lazyNamed(
  () => import('../Views/Admin/PublicTemplatesOverview'),
  'PublicTemplatesOverview',
);
const UserTemplatesOverview = lazyNamed(
  () => import('../Views/Admin/UserTemplatesOverview'),
  'UserTemplatesOverview',
);
const ModulesOverview = lazyNamed(
  () => import('../Views/Admin/ModulesOverview'),
  'ModulesOverview',
);
const TemplateEditor = lazyNamed(() => import('../Views/Admin/TemplateEditor'), 'TemplateEditor');
const BaseTemplateEditor = lazyNamed(
  () => import('../Views/Admin/BaseTemplateEditor'),
  'BaseTemplateEditor',
);
const PublicTemplateEditor = lazyNamed(
  () => import('../Views/Admin/PublicTemplateEditor'),
  'PublicTemplateEditor',
);
const SpecialtyEditor = lazyNamed(
  () => import('../Views/Admin/SpecialtyEditor'),
  'SpecialtyEditor',
);
const UserTemplateEditor = lazyNamed(
  () => import('../Views/Admin/UserTemplateEditor'),
  'UserTemplateEditor',
);
const ModuleEditor = lazyNamed(() => import('../Views/Admin/ModuleEditor'), 'ModuleEditor');
const ConfigView = lazyWithRetry(() => import('../Views/Admin/ConfigView'));
const ExtractionTestsView = lazyWithRetry(() => import('../Views/Admin/ExtractionTestsView'));
const AdminToolsView = lazyNamed(() => import('../Views/Admin/AdminToolsView'), 'AdminToolsView');
const NoteProcessingViewTests = lazyWithRetry(
  () => import('../Views/Admin/NoteProcessingTestsView'),
);
const GenerationSessionView = lazyNamed(
  () => import('../Views/Admin/GenerationSessionView'),
  'GenerationSessionView',
);
const GenerationSessionModelComparisonView = lazyNamed(
  () => import('../Views/Admin/GenerationSessionModelComparisonView'),
  'GenerationSessionModelComparisonView',
);
const ModuleCustomizationView = lazyNamed(
  () => import('../Views/ModuleCustomizationView'),
  'ModuleCustomizationView',
);
const AdminModulesPage = lazyWithRetry(
  () => import('../components/Admin/modules/AdminModulesPage'),
);
const AdminTemplatesPage = lazyWithRetry(
  () => import('../components/Admin/templates/AdminTemplatesPage'),
);
const UserOverview = lazyNamed(() => import('../Views/Admin/UsersOverview'), 'UserOverview');

export const adminRoutes: RouteObject[] = [
  {
    path: '/admin/',
    element: <AdminLayout />,
    errorElement: (
      <Auth0ProviderWithNavigate>
        <RootErrorBoundary />
      </Auth0ProviderWithNavigate>
    ),
    children: [
      {
        path: '/admin/users',
        element: (
          <ModeratorView>
            <SelectedUserProvider>
              <UserOverview />
            </SelectedUserProvider>
          </ModeratorView>
        ),
      },
      {
        path: '/admin/pdfuploads',
        element: (
          <AdminViewOnly>
            <UserUploadedTemplates />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/generateNoteForUser',
        element: (
          <AdminViewOnly>
            <GenerateNoteForUser />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/cohorts',
        element: (
          <AdminViewOnly>
            <CohortDefinitionsView />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/analysis',
        element: (
          <AdminViewOnly>
            <AnalysisSessionInspector />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/feedbacks/:user_id?',
        element: (
          <AdminViewOnly>
            <FeedbackOverview />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/feedback/:feedbackSessionId',
        element: (
          <AdminViewOnly>
            <AdminFeedbackSessionView />
          </AdminViewOnly>
        ),
        loader: AdminFeedbackSessionLoader,
      },
      {
        path: '/admin/notes/:user_id?',
        element: (
          <AdminViewOnly>
            <NotesOverview />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/note/:noteId',
        element: (
          <AdminViewOnly>
            <AdminNoteView />
          </AdminViewOnly>
        ),
        loader: ({ params }) => {
          if (!params.noteId) {
            throw new Error('Note ID is required');
          }
          return AdminNoteLoader(params.noteId);
        },
      },
      {
        path: '/admin/specialties',
        element: (
          <AdminViewOnly>
            <SpecialtiesOverview />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/templates',
        element: (
          <AdminViewOnly>
            <AdminTemplatesPage />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/basetemplates',
        element: (
          <AdminViewOnly>
            <BaseTemplatesOverview />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/publictemplates',
        element: (
          <AdminViewOnly>
            <PublicTemplatesOverview />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/usertemplates',
        element: (
          <AdminViewOnly>
            <UserTemplatesOverview />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/modules',
        element: (
          <AdminViewOnly>
            <AdminModulesPage />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/template/:templateId',
        element: (
          <AdminViewOnly>
            <TemplateEditor />
          </AdminViewOnly>
        ),
        loader: TemplateLoader,
      },
      {
        path: '/admin/basetemplate/:baseTemplateId',
        element: (
          <AdminViewOnly>
            <BaseTemplateEditor />
          </AdminViewOnly>
        ),
        loader: BaseTemplateLoader,
      },
      {
        path: '/admin/publictemplate/:templateId',
        element: (
          <AdminViewOnly>
            <PublicTemplateEditor />
          </AdminViewOnly>
        ),
        loader: PublicTemplateLoader,
      },
      {
        path: '/admin/specialty/:specialtyId',
        element: (
          <AdminViewOnly>
            <SpecialtyEditor />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/usertemplate/:templateId',
        element: (
          <AdminViewOnly>
            <UserTemplateEditor />
          </AdminViewOnly>
        ),
        loader: UserTemplateLoader,
      },
      {
        path: '/admin/module/:moduleId',
        element: (
          <AdminViewOnly>
            <ModuleEditor />
          </AdminViewOnly>
        ),
        loader: ModuleLoader,
      },
      {
        path: '/admin/config',
        element: (
          <AdminViewOnly>
            <ConfigView />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/extractionTests',
        element: (
          <AdminViewOnly>
            <ExtractionTestsView />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/tools/',
        element: (
          <AdminViewOnly>
            <AdminToolsView />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/noteProcessingTests/',
        element: (
          <AdminViewOnly>
            <NoteProcessingViewTests />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/generation',
        element: (
          <AdminViewOnly>
            <GenerationSessionView />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/generationComparison',
        element: (
          <AdminViewOnly>
            <GenerationSessionModelComparisonView />
          </AdminViewOnly>
        ),
      },
      {
        path: '/admin/moduleCustomization',
        element: (
          <AdminViewOnly>
            <ModuleCustomizationView />
          </AdminViewOnly>
        ),
      },
    ],
  },
];
